/**
 * "Still there?" — the mid-flow idle guard.
 *
 * A shared terminal holds a stranger's name and phone number on screen for
 * as long as they walk away from it. This asks once, briefly, then wipes the
 * flow. Asking first matters: a customer digging their phone out of a pocket
 * to type their number is idle by any timer's definition, and resetting
 * under them is its own failure.
 *
 * The countdown is a real one — the number on screen is the number of
 * seconds left, not decoration — because a silent reset looks like a crash.
 */

import { useEffect, useState } from 'react';
import { useCopy } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';

export default function IdlePrompt({
  seconds,
  onStay,
  onExpire,
}: {
  seconds: number;
  onStay: () => void;
  onExpire: () => void;
}) {
  const c = useCopy().kioskWalkIn.reset;
  const { t } = useShopConfig();
  const [left, setLeft] = useState(seconds);

  useEffect(() => {
    if (left <= 0) {
      onExpire();
      return;
    }
    const id = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left, onExpire]);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="kd-idle-title"
      // Stops the click from reaching the shell's own "any touch resets the
      // idle timer" handler, which would dismiss this without an answer.
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      className="fixed inset-0 z-50 grid place-items-center bg-navy/90 p-10 backdrop-blur-sm"
    >
      <div className="max-w-xl rounded-card bg-paper p-12 text-center">
        <h2 id="kd-idle-title" className="font-newsreader text-[44px] leading-tight text-navy">
          {t(c.idleHeadline)}
        </h2>
        <p className="mt-4 font-newsreader text-[22px] leading-relaxed text-slate-body">
          {t(c.idleBody, { seconds: left })}
        </p>
        <button
          type="button"
          onClick={onStay}
          className="mt-10 min-h-[64px] rounded-pill bg-gold px-14 py-5 font-newsreader text-[24px] text-navy"
        >
          {t(c.idleStay)}
        </button>
      </div>
    </div>
  );
}
